<template>
  <section class="section dashboard">
    <div class="row">
      <div class="col-lg-12">

        <draggable
            v-model="planCards"
            item-key="id"
            tag="div"
            class="row"
            handle=".drag-handle"
            @end="emit('drag', planCards)"
        >
          <template #item="{ element: plan }">
            <div class="col-xxl-4 col-md-6 mb-4">
              <div class="card info-card revenue-card w-100 h-100">

                <div class="filter d-flex align-items-center">
                  <span class="drag-handle me-2" style="cursor: grab;">
                    <i class="bi bi-grip-vertical"></i>
                  </span>

                  <ActionDropdown>
                    <li class="dropdown-header text-start">
                      <h6>Действия</h6>
                    </li>

                    <li>
                      <router-link
                          class="dropdown-item"
                          :to="{ name: 'PlanDetail', params: { id: plan.id } }"
                      >
                        Детальная
                      </router-link>
                    </li>
                    <li>
                      <a
                          @click.prevent="onDelete(plan)"
                          class="dropdown-item"
                          href="#"
                      >
                        Удалить
                      </a>
                    </li>
                  </ActionDropdown>
                </div>

                <div class="card-body">
                  <h5 class="card-title">
                    {{ plan.name }}
                    <span> | {{ plan.typeLabel }}</span>
                    <span> | {{ plan.scopeTypeLabel }}</span>
                  </h5>

                  <router-link
                      class="dropdown-item"
                      :to="{ name: 'PlanDetail', params: { id: plan.id } }"
                  >
                    <div class="d-flex align-items-center">
                      <div class="ps-3">
                        <span class="text-muted small pt-2 ps-1">Период </span>
                        <span class="small pt-1 fw-bold">
                          {{ plan.dateFrom }} - {{ plan.dateTo }}
                        </span>
                        <br>

                        <span class="text-muted small pt-2 ps-1">Счет </span>
                        <span class="small pt-1 fw-bold">{{ plan.accountName }}</span>
                        <br>

                        <span class="text-muted small pt-2 ps-1">{{ plan.statusLabel }}</span>
                        <span class="small pt-1 fw-bold">
                          {{ plan.transactionAmount }}/{{ plan.amount }} {{ plan.currencySymbol }} [{{ plan.percent + '%' }}]
                        </span>

                        <p class="text-muted medium pt-2 ps-1">
                          {{ plan.isOverAmount ? plan.overMessage : '\u00A0' }}
                        </p>


                      </div>
                    </div>
                  </router-link>

                  <div class="plan-progress-wrapper">
                    <div class="progress plan-progress">
                      <div
                          :class="{ 'progress-bar': true, [plan.barClass]: !!plan.barClass }"
                          role="progressbar"
                          :style="{ width: Math.min(plan.percent, 100) + '%' }"
                          :aria-valuenow="Math.min(plan.percent, 100)"
                          aria-valuemin="0"
                          aria-valuemax="100"
                      />
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </template>
        </draggable>

        <div class="row">
          <div class="col-xxl-4 col-md-6 mb-4">
            <div
                class="card info-card add-card w-100 h-100 d-flex align-items-center justify-content-center"
                @click="emit('add')"
                style="cursor: pointer;"
            >
              <div class="text-center">
                <h5 class="card-title">
                  <i class="bi bi-plus-lg" style="font-size: 48px;"></i>
                  <br>
                  Добавить
                </h5>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </section>
</template>

<script setup>
import {ref, onUnmounted, onMounted, watch} from 'vue';
import {formatLocal, getCurrencySymbol} from '@/api/helpers';
import {getProgressBar, getScopeTypeName, getTypeName} from "@/features/plans/composables/usePlan";
import draggable from 'vuedraggable';
import ActionDropdown from "@/components/ui/components/ActionDropdown.vue";

const props = defineProps({
  plans: { type: Array, required: true }
});

const emit = defineEmits(['delete', 'add', 'drag']);

const planCards = ref([]);

const buildPlanCards = (plans) => {
  planCards.value = plans.map(plan => {
    const progress = getProgressBar(plan);
    let barClass = '';
    let statusLabel = '';
    const currencySymbol = getCurrencySymbol(plan.accountCurrency);

    if (plan.type === 'EXPENSE') {
      if (progress.percent > 75) {
        barClass = 'bg-warning';
        statusLabel = '🟡 Близко к лимиту ';
      } else {
        barClass = 'bg-success';
        statusLabel = '🟢 В пределах ';
      }
      if (progress.percent >= 100) {
        barClass = 'progress-bar-striped bg-danger';
        statusLabel = '🔴 Перерасход ';
      }
    } else {
      if (progress.percent > 75) {
        barClass = 'plan-bar-income-medium';
        statusLabel = '🟡 Почти выполнен ';
      } else {
        barClass = 'plan-bar-income-low';
        statusLabel = '🟡 В процессе ';
      }
      if (progress.percent >= 100) {
        barClass = 'progress-bar-striped plan-bar-income-high';
        statusLabel = '🟢 План выполнен ';
      }
    }
    let overMessage = '';
    if (progress.isOverAmount) {
      progress.remainingAmount = Math.abs(progress.remainingAmount);
      overMessage = plan.type === 'EXPENSE'
          ? `- ${progress.remainingAmount} ${currencySymbol}`
          : `+ ${progress.remainingAmount} ${currencySymbol}`;
    }
    return {
      ...plan,
      ...progress,
      amount: progress.amount,
      dateFrom: formatLocal(plan.dateFrom, true),
      dateTo: formatLocal(plan.dateTo, true),
      barClass: barClass,
      overMessage: overMessage,
      scopeTypeLabel: getScopeTypeName(plan),
      typeLabel: getTypeName(plan),
      statusLabel: statusLabel,
      currencySymbol: currencySymbol,
    };
  });
};

watch(
    () => props.plans,
    (plans) => {
      buildPlanCards(plans)
    },
    { immediate: true }
);

function onDelete(plan) {
  emit('delete', plan);
}


</script>

<style scoped>
.plan-progress-wrapper {
  width: 90%;
  overflow: hidden;
}
</style>
